import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NumbersIcon from "@mui/icons-material/Numbers";
import PhoneIcon from "@mui/icons-material/Phone";
import VerifiedIcon from "@mui/icons-material/Verified";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ContractorRegistrationApiService from "../../../../infrastructure/api/contractorRegistration";
import { companyNationalIdInputProps } from "../../../../infrastructure/utils/companyNationalIdRules";
import {
  normalizeIban,
  onlyDigits,
  validateBankAccount,
  validateCardNumber,
  validateIban,
} from "../contractorBankFields";

const formatCities = (data) =>
  (data || []).map((c) => ({
    id: c.id,
    value: c.id,
    label: c.cityName,
    cityName: c.cityName,
    cityCode: c.cityCode,
    provinceId: c.provinceId,
    externalId: c.externalId,
    latitude: c.latitude,
    longitude: c.longitude,
  }));

const CompanyInfoStep = ({
  formData,
  setFormData,
  errors,
  setErrors,
  initialProvinces,
  initialCities,
  onAvailabilityCheck,
}) => {
  const [provinces, setProvinces] = useState(initialProvinces || []);
  const [allCities, setAllCities] = useState(initialCities ? formatCities(initialCities) : []);
  const [cities, setCities] = useState([]);
  const hasAllPrefetched = initialProvinces?.length && initialCities?.length;
  const [loading, setLoading] = useState(!hasAllPrefetched);
  const previousProvinceId = useRef(null);
  const [distributionCompanies, setDistributionCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const isInDistributionVendorList =
    formData.isInDistributionVendorList ||
    (Array.isArray(formData.distributionCompanyIds) &&
      formData.distributionCompanyIds.length > 0);

  useEffect(() => {
    let mounted = true;
    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const res = await ContractorRegistrationApiService.getDistributionCompanies();
        if (!mounted) return;
        const list = res?.data || res?.Data || [];
        setDistributionCompanies(Array.isArray(list) ? list : []);
      } catch (_e) {
        if (mounted) setDistributionCompanies([]);
      } finally {
        if (mounted) setLoadingCompanies(false);
      }
    };
    loadCompanies();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const needProvinces = !initialProvinces?.length;
        const needCities = !initialCities?.length;
        if (!needProvinces && !needCities) {
          setLoading(false);
          return;
        }
        if (needProvinces || needCities) setLoading(true);
        const [provincesRes, citiesRes] = await Promise.all([
          needProvinces ? ContractorRegistrationApiService.getProvinces() : Promise.resolve({ isSuccess: true, data: initialProvinces || [] }),
          needCities ? ContractorRegistrationApiService.getCities() : Promise.resolve({ isSuccess: true, data: initialCities || [] }),
        ]);

        if (provincesRes?.isSuccess && provincesRes?.data) {
          setProvinces(provincesRes.data);
        }
        if (citiesRes?.isSuccess && citiesRes?.data) {
          setAllCities(formatCities(citiesRes.data));
        }
      } catch (error) {
        console.error("خطا در دریافت اطلاعات:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // فیلتر شهرستان‌ها وقتی استان تغییر می‌کند
  useEffect(() => {
    if (allCities.length > 0) {
      if (formData.provinceId) {
        const filteredCities = allCities.filter(city =>
          city.provinceId === Number(formData.provinceId)
        );
        setCities(filteredCities);

        // اگر استان تغییر کرده باشد، شهرستان انتخاب شده را چک کن
        if (previousProvinceId.current !== null &&
            previousProvinceId.current !== formData.provinceId) {
          const selectedCityExists = filteredCities.some(city => city.id === formData.cityId);
          if (!selectedCityExists && formData.cityId) {
            setFormData(prev => ({ ...prev, cityId: null, city: "" }));
          }
        }
      } else {
        setCities([]);
        // شهرستان را پاک کن اگر استان انتخاب نشده باشد
        if (previousProvinceId.current !== null) {
          setFormData(prev => ({ ...prev, cityId: null, city: "" }));
        }
      }
    }

    // بروزرسانی previousProvinceId
    previousProvinceId.current = formData.provinceId;
  }, [formData.provinceId, allCities, formData.cityId, setFormData]);
  const handleChange = (field) => (event) => {
    let value = event.target.value;

    // اطمینان از نوع عددی برای استان و شهر
    if (field === "provinceId" || field === "cityId") {
      value = value === "" ? null : Number(value);
    }

    if (field === "ibanNumber") {
      value = normalizeIban(value);
    } else if (field === "bankAccountNumber" || field === "cardNumber") {
      value = onlyDigits(value);
    }

    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      if (field === "cityId") {
        const selectedCity = allCities.find((city) => city.id === value);
        updated.city = selectedCity?.cityName || "";
      }

      if (field === "provinceId" && prev.provinceId !== value) {
        updated.city = "";
      }

      return updated;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const isIndividual = formData.registrationType === "individual";
  const requiredMark = isIndividual ? "" : " *";
  // فیلدهای مشترک صدور قرارداد (آدرس/موقعیت) برای حقیقی و حقوقی الزامی‌اند
  const locationRequiredMark = " *";

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        {isIndividual ? "اطلاعات تماس و موقعیت" : "اطلاعات شرکت"}
      </Typography>

      {/* فقط برای حقوقی نمایش داده شود */}
      {!isIndividual && (
        <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.companyName}>
            <InputLabel>نام شرکت{requiredMark}</InputLabel>
            <OutlinedInput
              value={formData.companyName}
              onChange={handleChange("companyName")}
              startAdornment={
                <InputAdornment position="start">
                  <BusinessIcon color="primary" />
                </InputAdornment>
              }
              label={`نام شرکت${requiredMark}`}
            />
            {errors.companyName && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.companyName}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.companyRegistrationNumber}>
            <InputLabel>شماره ثبت شرکت{requiredMark}</InputLabel>
            <OutlinedInput
              value={formData.companyRegistrationNumber}
              onChange={handleChange("companyRegistrationNumber")}
              inputProps={{ maxLength: 6 }}
              startAdornment={
                <InputAdornment position="start">
                  <NumbersIcon color="primary" />
                </InputAdornment>
              }
              label={`شماره ثبت شرکت${requiredMark}`}
            />
            {errors.companyRegistrationNumber && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.companyRegistrationNumber}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={`شناسه ملی شرکت${requiredMark}`}
            value={formData.companyNationalId}
            onChange={handleChange("companyNationalId")}
            onBlur={(event) =>
              onAvailabilityCheck?.("companyNationalId", {
                companyNationalId: event.target.value,
              })
            }
            inputProps={companyNationalIdInputProps}
            error={!!errors.companyNationalId}
            helperText={errors.companyNationalId || "۱۱ رقم"}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.companyPhone}>
            <InputLabel>تلفن شرکت{requiredMark}</InputLabel>
            <OutlinedInput
              value={formData.companyPhone}
              onChange={handleChange("companyPhone")}
              inputProps={{ maxLength: 11 }}
              startAdornment={
                <InputAdornment position="start">
                  <PhoneIcon color="primary" />
                </InputAdornment>
              }
              label={`تلفن شرکت${requiredMark}`}
              placeholder="021-12345678"
            />
            {errors.companyPhone && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.companyPhone}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="سال تاسیس *"
            value={formData.foundedYear || ""}
            onChange={handleChange("foundedYear")}
            type="number"
            inputProps={{ min: 1200, max: 1600 }}
            error={!!errors.foundedYear}
            helperText={errors.foundedYear || "مثال: 1401"}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="وب‌سایت (اختیاری)"
            value={formData.companyWebsite || ""}
            onChange={handleChange("companyWebsite")}
            placeholder="https://example.com"
            error={!!errors.companyWebsite}
            helperText={errors.companyWebsite || "در صورت وجود وارد کنید"}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="کد پستی *"
            value={formData.postalCode}
            onChange={handleChange("postalCode")}
            inputProps={{ maxLength: 10 }}
            error={!!errors.postalCode}
            helperText={errors.postalCode}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="سابقه کار (سال) *"
            value={formData.workExperience}
            onChange={handleChange("workExperience")}
            type="number"
            inputProps={{ min: 0, max: 50 }}
            error={!!errors.workExperience}
            helperText={errors.workExperience}
          />
        </Grid>
      </Grid>
      )}

      {/* فیلدهای آدرس برای هر دو نوع ثبت‌نام */}
      <Grid container spacing={3} sx={{ mt: isIndividual ? 2 : 2 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.provinceId}>
            <InputLabel>استان{locationRequiredMark}</InputLabel>
            <Select
              value={formData.provinceId || ""}
              onChange={handleChange("provinceId")}
              label={`استان${locationRequiredMark}`}
            >
              {provinces.map((province) => (
                <MenuItem key={province.id} value={province.id}>
                  {province.title}
                </MenuItem>
              ))}
            </Select>
            {errors.provinceId && (
              <FormHelperText>{errors.provinceId}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.cityId}>
            <InputLabel>شهرستان{locationRequiredMark}</InputLabel>
            <Select
              value={formData.cityId || ""}
              onChange={handleChange("cityId")}
              label={`شهرستان${locationRequiredMark}`}
              disabled={!formData.provinceId}
            >
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.cityName}
                </MenuItem>
              ))}
            </Select>
            {!formData.provinceId && (
              <FormHelperText>ابتدا استان را انتخاب کنید</FormHelperText>
            )}
            {errors.cityId && (
              <FormHelperText>{errors.cityId}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.companyAddress}>
            <InputLabel>
              {isIndividual
                ? `آدرس${locationRequiredMark}`
                : `آدرس شرکت${locationRequiredMark}`}
            </InputLabel>
            <OutlinedInput
              value={formData.companyAddress}
              onChange={handleChange("companyAddress")}
              inputProps={{ maxLength: 200 }}
              multiline
              rows={3}
              startAdornment={
                <InputAdornment position="start">
                  <LocationOnIcon color="primary" />
                </InputAdornment>
              }
              label={
                isIndividual
                  ? `آدرس${locationRequiredMark}`
                  : `آدرس شرکت${locationRequiredMark}`
              }
            />
            {errors.companyAddress && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.companyAddress}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {isIndividual && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="کد پستی *"
              value={formData.postalCode}
              onChange={handleChange("postalCode")}
              inputProps={{ maxLength: 10 }}
              error={!!errors.postalCode}
              helperText={errors.postalCode}
            />
          </Grid>
        )}

        {isIndividual && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="سابقه کار (سال) *"
              value={formData.workExperience}
              onChange={handleChange("workExperience")}
              type="number"
              inputProps={{ min: 0, max: 50 }}
              error={!!errors.workExperience}
              helperText={errors.workExperience}
            />
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceIcon color="primary" />
            اطلاعات بانکی
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label="شماره شبا"
            value={formData.ibanNumber || ""}
            onChange={handleChange("ibanNumber")}
            placeholder="IR120170000000123456789012"
            inputProps={{ maxLength: 26 }}
            error={!!errors.ibanNumber}
            helperText={errors.ibanNumber || "با پیشوند IR"}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label="شماره حساب"
            value={formData.bankAccountNumber || ""}
            onChange={handleChange("bankAccountNumber")}
            inputProps={{ maxLength: 20, inputMode: "numeric" }}
            error={!!errors.bankAccountNumber}
            helperText={errors.bankAccountNumber}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label="شماره کارت"
            value={formData.cardNumber || ""}
            onChange={handleChange("cardNumber")}
            inputProps={{ maxLength: 16, inputMode: "numeric" }}
            error={!!errors.cardNumber}
            helperText={errors.cardNumber || "۱۶ رقم"}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: isIndividual ? 0 : 2 }}>
        <Grid item xs={12}>
          <Box
            sx={{
              p: 2,
              bgcolor: "info.lighter",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "info.light",
            }}
          >
            <Typography variant="body2" color="info.main" sx={{ mb: 1, fontWeight: 600 }}>
              اعلام آمادگی برای بازدید اولیه
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              در صورت داشتن آمادگی برای انجام بازدید اولیه و تعیین زمان بازدید، این گزینه را فعال کنید.
              در غیر این صورت، بازدید اولیه توسط ناظر انجام خواهد شد.
            </Typography>
            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.canPerformSiteVisit || false}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        canPerformSiteVisit: e.target.checked,
                      }));
                    }}
                  />
                }
                label="آمادگی انجام بازدید اولیه را دارم"
              />
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
            >
              <VerifiedIcon color="primary" fontSize="small" />
              تأییدیه‌ها و عضویت در فهرست‌های رسمی
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              در صورت عضویت در فهرست‌های رسمی زیر، گزینه‌های مربوط را مشخص کنید.
              این اطلاعات در بررسی درخواست توسط مدیر استفاده می‌شود.
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.isSatbaApproved}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        isSatbaApproved: e.target.checked,
                      }));
                    }}
                  />
                }
                label="در لیست پیمانکاران مورد تایید ساتبا هستم"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!isInDistributionVendorList}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        isInDistributionVendorList: checked,
                        distributionCompanyIds: checked
                          ? prev.distributionCompanyIds || []
                          : [],
                      }));
                    }}
                  />
                }
                label="در وندور لیست شرکت‌های توزیع برق هستم"
              />
            </FormGroup>

            <Collapse in={!!isInDistributionVendorList} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary.light",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(25, 118, 210, 0.08)"
                      : "rgba(25, 118, 210, 0.04)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <ElectricBoltIcon fontSize="small" color="warning" />
                  انتخاب شرکت‌های توزیع
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  شرکت‌هایی که در وندور لیست آن‌ها قرار دارید را انتخاب کنید.
                </Typography>

                {loadingCompanies ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : distributionCompanies.length === 0 ? (
                  <Alert severity="warning">
                    لیست شرکت‌های توزیع در دسترس نیست. لطفاً بعداً دوباره تلاش کنید.
                  </Alert>
                ) : (
                  <>
                    <Box
                      sx={{
                        maxHeight: 280,
                        overflowY: "auto",
                        pr: 1,
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                        },
                        gap: 0.5,
                      }}
                    >
                      {distributionCompanies.map((company) => {
                        const companyId = Number(company.id ?? company.Id);
                        const title = company.title || company.Title || "";
                        const selected = (
                          formData.distributionCompanyIds || []
                        ).includes(companyId);
                        return (
                          <FormControlLabel
                            key={companyId}
                            control={
                              <Checkbox
                                size="small"
                                checked={selected}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setFormData((prev) => {
                                    const current = prev.distributionCompanyIds || [];
                                    const next = checked
                                      ? [...current, companyId]
                                      : current.filter((id) => id !== companyId);
                                    return {
                                      ...prev,
                                      distributionCompanyIds: next,
                                    };
                                  });
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2">{title}</Typography>
                            }
                          />
                        );
                      })}
                    </Box>
                    {(formData.distributionCompanyIds || []).length > 0 && (
                      <Box
                        sx={{
                          mt: 1.5,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.75,
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          انتخاب‌شده ({formData.distributionCompanyIds.length}):
                        </Typography>
                        {formData.distributionCompanyIds.map((id) => {
                          const company = distributionCompanies.find(
                            (c) => Number(c.id ?? c.Id) === Number(id)
                          );
                          return (
                            <Chip
                              key={id}
                              size="small"
                              label={company?.title || company?.Title || id}
                              onDelete={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  distributionCompanyIds: (
                                    prev.distributionCompanyIds || []
                                  ).filter((x) => x !== id),
                                }))
                              }
                            />
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Collapse>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            * فیلدهای اجباری
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyInfoStep;
